import { ColorSchemeScript, MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import { Notifications } from '@mantine/notifications';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from 'react-router';
import {
  getMarketingLocale,
  marketingLocaleMiddleware,
} from '#marketing/lib/marketing-i18n.server.js';
import { marketingHtmlLang } from '#marketing/lib/marketing-locale.js';
import { MarketingLocaleSync } from '#marketing/components/MarketingLocaleSync.jsx';
import { theme } from '#lib/theme.js';
import stylesheet from './app.css?url';

export const links = () => [
  { rel: 'stylesheet', href: stylesheet },
  { rel: 'icon', href: '/favicon.ico' }
];

export const middleware = [marketingLocaleMiddleware];

/**
 * Root loader: locale for `<html lang>` and prerender context.
 *
 * @param {object} args
 * @param {import('react-router').RouterContextProvider} args.context
 */
export function loader({ context }) {
  const locale = getMarketingLocale(context);
  return { locale, htmlLang: marketingHtmlLang(locale) };
}

/**
 * Root document shell for the static marketing site (Cloudflare Pages).
 */
export default function MarketingRoot() {
  const { htmlLang } = useLoaderData();
  return (
    <html lang={htmlLang} data-mantine-color-scheme="light">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <ColorSchemeScript defaultColorScheme="light" />
      </head>
      <body>
        <MantineProvider defaultColorScheme="light" theme={theme}>
          <MarketingLocaleSync />
          <Notifications position="top-right" autoClose={4000} limit={3} />
          <Outlet />
        </MantineProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }) {
  let message = 'Something went wrong';
  let details = 'Please try again or return to the home page.';

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? 'Page not found' : 'Error';
    details =
      error.status === 404
        ? 'The page you requested does not exist.'
        : error.statusText || details;
  }

  return (
    <main style={{ padding: '2rem', maxWidth: '40rem', margin: '0 auto' }}>
      <h1>{message}</h1>
      <p>{details}</p>
      <p>
        <a href="/">Back to PAC Hub</a>
      </p>
    </main>
  );
}
